"use client";

import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faClose,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";

import Navigation from "components/Navigation";
import TableOfContents from "components/TableOfContents";
import Stepper from "components/Stepper";

const Search = dynamic(() => import("components/Search"), {
  loading: () => "Loading...",
});

export default function PostLayout({
  children,
  toc = { isFixed: false, navigation: [] },
  stepper = { next: null, prev: null },
  onBodyScrollUpdate,
}) {
  const router = useRouter();
  const [panelOpen, setPanelopen] = useState(false);
  const hasStepper = Boolean(stepper?.prev || stepper?.next);

  const togglePanel = (active) => {
    if (typeof onBodyScrollUpdate === "function") onBodyScrollUpdate(!active);
    setPanelopen(active);
  };

  useEffect(() => {
    togglePanel(false);
  }, [router]);

  return (
    <>
      <button
        type="button"
        onClick={() => togglePanel(!panelOpen)}
        className="sticky z-30 top-0 flex justify-between w-full c-container lg:hidden py-4 text-white text-sm bg-gray-900 border-b border-gray-800"
      >
        <span className="space-x-2 cursor-pointer">
          <FontAwesomeIcon icon={faBook} className="w-3 h-3" />
          <span>Docs navigation</span>
        </span>

        <span className="flex items-center justify-center w-6 h-6 border border-white/15 rounded-sm cursor-pointer">
          {panelOpen ? (
            <FontAwesomeIcon icon={faClose} className="w-4 h-4 text-white/75" />
          ) : (
            <FontAwesomeIcon
              icon={faChevronDown}
              className="w-3 h-3 text-white/75"
            />
          )}
        </span>
      </button>

      <div className="c-container my-6 lg:my-12">
        <div className="lg:flex lg:flex-row lg:gap-x-10 mx-auto">
          <div className="l-nav" id="navigation">
            <div className={`mb-6 lg:mb-10 ${panelOpen ? "hidden" : "block"}`}>
              <Search />
            </div>

            <div
              className={`${
                panelOpen ? "z-50 block c-container overflow-y-auto" : "hidden"
              } lg:block fixed lg:static inset-0 top-[120px] h-screen lg:h-auto w-screen lg:w-auto pt-8 lg:pt-0 pb-40 lg:pb-0 bg-gray-900 lg:bg-transparent`}
            >
              <Navigation />
            </div>
          </div>

          <div className="l-main" id="main" data-role="main-content">
            <div className="bg-white lg:rounded-lg lg:overflow-hidden lg:shadow-sm lg:border lg:border-gray-200">
              <div className="lg:py-6 lg:px-8">{children}</div>

              {hasStepper && (
                <Stepper next={stepper.next} prev={stepper.prev} />
              )}
            </div>
          </div>

          <div className="l-toc hidden xl:block" id="toc">
            <div className={toc?.isFixed ? "xl:sticky top-[97px]" : ""}>
              <TableOfContents data={toc?.navigation || []} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

PostLayout.propTypes = {
  children: PropTypes.node,
  toc: PropTypes.object,
  stepper: PropTypes.shape({
    next: PropTypes.object,
    prev: PropTypes.object,
  }),
  onBodyScrollUpdate: PropTypes.func,
};
